import {
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  setDoc,
  deleteDoc,
} from "@firebase/firestore";
import { db } from "../../connection";
import { uploadImage } from "./website";
import logger from "../../../../utils/logger";
import { deleteImageFromStorage } from "../../../general/images";
import { convertPseudoJsonStringToObject } from "../../../../utils/helper";
import { getTransactionType } from "../../../../components/admin/purchases/purchaseUtils";

export function fbGetPartners(groupId) {
  return new Promise((resolve, reject) => {
    getDocs(collection(db, "partners", groupId, "details"))
      .then((querySnapshot) => {
        let dataArr = [];
        querySnapshot.docs.map((doc) => {
          let data = doc.data() || {};
          data.id = doc.id;
          let ytdField = `${new Date().getFullYear()}totalDonations`;
          let totalDonations = data.totalDonations || {};
          data.currentYearDonations = 0;
          data.totalDonations = Object.values(totalDonations).reduce(
            (acc, val) => {
              acc = acc + val;
              return acc;
            },
            0,
          );
          if (totalDonations[ytdField]) {
            data.currentYearDonations = totalDonations[ytdField];
          }
          dataArr.push(data);
        });
        resolve(dataArr);
      })
      .catch(() => {
        let message = "There was an error fetching the partners";
        reject({ message });
      });
  });
}

export function getPartnerDetails(groupId, docId) {
  return new Promise((resolve, reject) => {
    getDoc(doc(db, "partners", groupId, "details", docId))
      .then((doc) => {
        let data = {};
        if (doc.exists()) {
          data = doc.data();
          data.id = doc.id;
        }
        resolve(data);
      })
      .catch(() => {
        let message = "There was an error fetching the partners";
        reject({ message });
      });
  });
}

export function fbSavePartnerDetails(data, groupId, docId) {
  return new Promise((res, rej) => {
    (async () => {
      let docRef = doc(collection(db, "partners", groupId, "details"));
      if (docId) {
        docRef = doc(db, "partners", groupId, "details", docId);
      }

      data.showLogo = false;
      data.showQrImage = false;

      if (data.logo) {
        if (typeof data.logo === "object") {
          await uploadImage(
            data.logo,
            `${groupId}/partner/${docRef.id}/logo`,
            0,
          );
          data.showLogo = true;
        } else if (typeof data.logo === "string") {
          data.showLogo = true;
        }
      }

      if (data.qrImage) {
        if (typeof data.qrImage === "object") {
          await uploadImage(
            data.qrImage,
            `${groupId}/partner/${docRef.id}/qrImage`,
            0,
          );
          data.showQrImage = true;
        } else if (typeof data.qrImage === "string") {
          data.showQrImage = true;
        }
      }

      let galleryImageUrls = [];
      let imageIndex = 0;
      for (var image of data.galleryImages || []) {
        if (image !== null && image !== undefined && image !== "") {
          if (typeof image === "object") {
            let downloadUrl = await uploadImage(
              image,
              `${groupId}/partner/${docRef.id}/gallery/${imageIndex}`,
              imageIndex,
            ).then(({ downloadUrl }) => {
              return downloadUrl;
            });
            galleryImageUrls.push(downloadUrl);
          } else if (typeof image === "string") {
            galleryImageUrls.push(image);
          }
        } else {
          const galleryNames = data.galleryFiles.map((file) => file.name);
          await deleteImageFromStorage(
            `${groupId}/partner/${docRef.id}/gallery/${galleryNames[imageIndex]}`,
          );
          galleryImageUrls.splice(imageIndex, 1);
        }
        imageIndex++;
      }

      data.galleryImageCount = galleryImageUrls.length;

      let updateData = {
        name: data.name,
        partnerWebsite: data.partnerWebsite || "",
        details: data.details,
        showLogo: data.showLogo,
        showQrImage: data.showQrImage,
        galleryImageCount: data.galleryImageCount,
        discounts: data.discounts,
        collectDonations: data.collectDonations,
        suggestedAmounts: data.suggestedAmounts,
        displayOn: data.displayOn,
        processingFee: data.processingFee,
        status: data.status,
        discountAmount: /^\d+(\.\d+)?$/.test(data.discountAmount)
          ? data.discountAmount + "%"
          : data.discountAmount,
        isLogoTransparent: data.isLogoTransparent === true,
      };

      await setDoc(docRef, updateData, {
        merge: true,
      });
      res();
    })().catch(rej);
  });
}

export function fbDeletePartner(groupId, docId) {
  let docRef = doc(db, "partners", groupId, "details", docId);
  return new Promise((res, rej) => {
    deleteDoc(docRef).then(res).catch(rej);
  });
}

export function setPartnerSortOrder(idOrderMap, groupId) {
  return new Promise((resolve, reject) => {
    let collectionRef = collection(db, "partners", groupId, "details");

    let writePromises = Object.entries(idOrderMap).map((entry) =>
      updateDoc(doc(collectionRef, entry[0]), { sortIndex: entry[1] }),
    );
    Promise.all(writePromises)
      .then(() => resolve())
      .catch(reject);
  });
}

export async function fbGetDonationDataForPartner(
  groupId,
  partnerId,
  partnerName,
) {
  try {
    if (!groupId || !partnerId || !partnerName) {
      throw new Error("Group ID, partner ID, and partner name are required");
    }
    const collectionRef = collection(
      db,
      "partners",
      groupId,
      "details",
      partnerId,
      "donations",
    );

    const data = [];

    const snap = await getDocs(collectionRef);
    snap.docs.forEach((doc) => {
      const docData = doc.data() || {};
      (docData.purchaseDetails || []).forEach((detail) => {
        data.push({
          userId: doc.id,
          userName: docData.userName,
          userEmail: docData.userEmail,
          partnerName,
          partnerId,
          amount: detail.totalCost,
          date: detail.timestamp,
          paymentIntentId: detail.paymentIntentId || "",
          source: detail.source,
        });
      });
    });

    return data;
  } catch (error) {
    logger.error(error);
    return [];
  }
}

export async function fbGetDonationDataForGroupOld(groupId) {
  try {
    if (!groupId) {
      throw new Error("Group ID is required");
    }
    let collectionRef = collection(db, "partners", groupId, "details");
    let donations = [];

    let snap = await getDocs(collectionRef);
    for (let doc of snap.docs) {
      let docData = doc.data() || {};
      let partnerName = docData.name;
      let donationForPartner = await fbGetDonationDataForPartner(
        groupId,
        doc.id,
        partnerName,
      );
      donations.push(...(donationForPartner || []));
    }
    return donations;
  } catch (error) {
    logger.error(error);
    return [];
  }
}

export async function fbGetDonationDataForGroup(groupId) {
  try {
    if (!groupId) {
      throw new Error("Group ID is required");
    }
    const collectionRef = collection(
      db,
      "transaction_record",
      groupId,
      "records",
    );
    const donations = [];
    const seenPaymentIntents = new Set();

    const pushDonation = (record) => {
      const id = record?.paymentIntentId;
      if (id) {
        if (seenPaymentIntents.has(id)) return;
        seenPaymentIntents.add(id);
      }
      donations.push(record);
    };

    const parseDonationEntries = (rawDonation) => {
      if (!rawDonation) return [];
      if (Array.isArray(rawDonation)) return rawDonation;

      if (typeof rawDonation === "string") {
        const converted = convertPseudoJsonStringToObject(rawDonation);
        if (Array.isArray(converted)) return converted;
        if (converted && typeof converted === "object")
          return Object.values(converted);
        return [];
      }

      if (typeof rawDonation === "object") {
        return Object.values(rawDonation);
      }

      return [];
    };

    const partnerCollectionRef = collection(db, "partners", groupId, "details");
    const partnerSnap = await getDocs(partnerCollectionRef);
    const partnerMap = new Map();
    partnerSnap.docs.forEach((partnerDoc) => {
      const partnerData = partnerDoc.data() || {};
      partnerMap.set(partnerDoc.id, partnerData.name);
    });

    const snap = await getDocs(collectionRef);
    for (const doc of snap.docs) {
      const transaction = doc.data() || {};
      const type = getTransactionType(transaction);

      if (type !== "registration" && type !== "giveaway") {
        continue;
      }

      const baseRecord = {
        paymentIntentId: doc.id,
        userName: transaction?.name,
        userEmail: transaction?.email,
        date: transaction?.timestamp,
        source: type || "transaction_record",
      };

      if (type === "registration") {
        const rawDonation =
          transaction?.data?.donations ||
          transaction?.data?.donationData ||
          transaction?.metadata?.donations ||
          transaction?.metadata?.donationData;
        const donationEntries = parseDonationEntries(rawDonation);

        donationEntries.forEach((entry) => {
          const amount = Number(entry?.amount) || 0;
          if (amount <= 0) return;

          pushDonation({
            ...baseRecord,
            amount,
            partnerId: entry?.partnerId,
            partnerName:
              entry?.partnerName ||
              entry?.name ||
              (entry?.partnerId ? partnerMap.get(entry.partnerId) : null) ||
              transaction?.data?.partnerName ||
              transaction?.metadata?.partnerName,
            donationName: entry?.name,
          });
        });
      }

      if (type === "giveaway") {
        let giveawayParams = null;
        try {
          const giveawayParamsString =
            transaction?.giveawayParams || transaction?.data?.giveawayParams;
          if (giveawayParamsString) {
            if (typeof giveawayParamsString === "string") {
              giveawayParams = JSON.parse(giveawayParamsString);
            } else {
              giveawayParams = giveawayParamsString;
            }
          }
        } catch (error) {
          logger.error("Error parsing giveawayParams:", error);
        }

        if (giveawayParams) {
          const giveawayBaseRecord = {
            ...baseRecord,
            userName: giveawayParams?.userName || baseRecord.userName,
            userEmail: giveawayParams?.userEmail || baseRecord.userEmail,
          };

          const rawDonation =
            giveawayParams?.donations ||
            giveawayParams?.donationData ||
            transaction?.data?.donations ||
            transaction?.data?.donationData ||
            transaction?.metadata?.donations ||
            transaction?.metadata?.donationData;
          const donationEntries = parseDonationEntries(rawDonation);

          if (donationEntries.length > 0) {
            // Process donation entries from giveawayParams
            donationEntries.forEach((entry) => {
              const amount = Number(entry?.amount) || 0;
              if (amount <= 0) return;

              const entryPartnerId =
                entry?.partnerId || giveawayParams?.partnerId;
              pushDonation({
                ...giveawayBaseRecord,
                amount,
                partnerId: entryPartnerId,
                partnerName:
                  entry?.partnerName ||
                  entry?.name ||
                  (entryPartnerId ? partnerMap.get(entryPartnerId) : null) ||
                  transaction?.data?.partnerName ||
                  transaction?.metadata?.partnerName,
                donationName: entry?.name,
              });
            });
          }

          const amount = Number(giveawayParams?.totalCost) || 0;
          if (amount > 0 && giveawayParams?.partnerId) {
            pushDonation({
              ...giveawayBaseRecord,
              amount,
              partnerId: giveawayParams?.partnerId,
              partnerName:
                partnerMap.get(giveawayParams.partnerId) ||
                transaction?.data?.partnerName ||
                transaction?.metadata?.partnerName,
              donationName: giveawayParams?.giveawayName,
            });
          }
        }
      }
    }

    for (const partnerDoc of partnerSnap.docs) {
      const partnerData = partnerDoc.data() || {};
      const partnerName = partnerData.name;
      const partnerDonations = await fbGetDonationDataForPartner(
        groupId,
        partnerDoc.id,
        partnerName,
      );
      (partnerDonations || []).forEach((d) => pushDonation(d));
    }

    return donations;
  } catch (error) {
    logger.error(error);
    return [];
  }
}
